import { formatarData } from 'src/app/core/utils/utilities';
import { Component, Inject, OnInit } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef
} from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CaixaService } from 'src/app/services/CaixaService/caixa.service';
import { IDadosFechamento } from './modal-fechamento-caixa.model';
import { SwalAlertService } from 'src/app/services/SwalAlertService/swalAlert.service';
import { AppExportBase64 } from 'src/app/services/ExportBase64/exportBase64.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-modal-fechamento-caixa',
  templateUrl: './modal-fechamento-caixa.component.html',
  styleUrls: ['./modal-fechamento-caixa.component.scss']
})
export class ModalFechamentoCaixaComponent implements OnInit {
  isLoading: boolean = false;
  form: FormGroup;

  dadosFechamento: IDadosFechamento = {
    DATA_ABERTURA: '--',
    PREVISAO_FECHAMENTO: '--',
    VALOR_ABERTURA: '--',
    DATA_FECHAMENTO: formatarData(new Date()),
    VALOR_FECHAMENTO: '--'
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<ModalFechamentoCaixaComponent>,
    private fb: FormBuilder,
    private caixaService: CaixaService,
    private appExportBase64: AppExportBase64,
    private swalAlertService: SwalAlertService
  ) {
    this.form = this.initForm();
  }

  ngOnInit(): void {
    this.activateRoute();
  }

  initForm(): FormGroup {
    return this.fb.group({
      VL_FECH_DINH_CAIX: [0, [Validators.required, Validators.min(0)]],
      VL_FECH_DEBT_CAIX: [0, [Validators.required, Validators.min(0)]],
      VL_FECH_CRDT_CAIX: [0, [Validators.required, Validators.min(0)]],
      VL_FECH_PIX_CAIX: [0, [Validators.required, Validators.min(0)]],
      VL_FECH_APRZ_CAIX: [0, [Validators.required, Validators.min(0)]]
    });
  }

  postFechamento() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    const PK_CAIX = this.data.PK_CAIX;
    const PermissionToken = this.data.PermissionToken;
    this.caixaService
      .PatchCaixaStatus({
        PK_CAIX,
        PermissionToken,
        VL_FECH_DINH_CAIX: this.getNumericFormValue('VL_FECH_DINH_CAIX'),
        VL_FECH_DEBT_CAIX: this.getNumericFormValue('VL_FECH_DEBT_CAIX'),
        VL_FECH_CRDT_CAIX: this.getNumericFormValue('VL_FECH_CRDT_CAIX'),
        VL_FECH_PIX_CAIX: this.getNumericFormValue('VL_FECH_PIX_CAIX'),
        VL_FECH_APRZ_CAIX: this.getNumericFormValue('VL_FECH_APRZ_CAIX')
      })
      .subscribe({
        next: res => {
          this.caixaService.GetReceiptCaixaById(PK_CAIX).subscribe({
          next: receiptRes => {
            this.appExportBase64.downloadPDF(
              receiptRes.ReportBase64,
              `Relatório do Caixa - ${PK_CAIX}`
            );
            Swal.fire({
              title: 'Caixa fechado com sucesso!',
              icon: 'success',
              confirmButtonColor: 'green',
              confirmButtonText: 'Ok'
            }).then(() => {
              this.dialogRef.close();
              location.reload();
            });
          },
          error: err => {
            Swal.fire({
              title: 'Caixa fechado com sucesso!',
              text: 'Atenção: Não foi possível baixar o relatório automaticamente.',
              icon: 'warning',
              confirmButtonColor: 'green',
              confirmButtonText: 'Ok'
            }).then(() => {
              this.dialogRef.close();
              location.reload();
            });
          }
          });
        },
        error: err => {
          this.isLoading = false;
          const errorMessage =
            err.error?.Errors?.Error ||
            err.error?.Error ||
            'Ocorreu um erro ao fechar o caixa.';
          this.swalAlertService.showAlert('Erro!', errorMessage, 'error', true);
        }
      });
  }

  onCancelar(): void {
    this.dialogRef.close();
    location.reload();
  }

  getNumericFormValue(controlName: string): number {
    return Number(this.form.get(controlName)?.value ?? 0);
  }

  getCaixa(PK_CAIX: number): void {
    this.isLoading = true;
    if (!PK_CAIX) return;
    this.caixaService.GetCaixaById(PK_CAIX).subscribe({
      next: res => {
        this.dadosFechamento.DATA_ABERTURA = formatarData(res.DT_ABTR_CAIX);
        this.dadosFechamento.PREVISAO_FECHAMENTO = this.calcPrevisaoFechamento(
          res.DT_ABTR_CAIX
        );
        this.dadosFechamento.VALOR_ABERTURA = res.VL_ABTR_CAIX.toLocaleString(
          'pt-BR',
          {
            style: 'currency',
            currency: 'BRL'
          }
        );
        this.isLoading = false;
      },
      error: err => {
        this.isLoading = false;
      }
    });
  }

  getValorFechamento(PK_CAIX: number): void {
    this.isLoading = true;
    if (!PK_CAIX) return;
    const params = {
      PK_CAIX
    };
    this.caixaService.GetCaixaTotalizadorById(params).subscribe({
      next: res => {
        this.dadosFechamento.VALOR_FECHAMENTO = res.VL_TOTL_CAIX.toLocaleString(
          'pt-BR',
          {
            style: 'currency',
            currency: 'BRL'
          }
        );
        this.isLoading = false;
      },
      error: err => {
        this.isLoading = false;
      }
    });
  }

  calcPrevisaoFechamento(DT_ABTR_CAIX: string): string {
    const dataAbertura = new Date(DT_ABTR_CAIX);
    const previsaoFechamento = new Date(dataAbertura.getTime());
    previsaoFechamento.setHours(previsaoFechamento.getHours() + 12);
    return formatarData(previsaoFechamento);
  }

  activateRoute(): void {
    const PK_CAIX = this.data.PK_CAIX;
    this.getCaixa(PK_CAIX);
    this.getValorFechamento(PK_CAIX);
  }
}
